import * as THREE from 'three';
import { roundNumber } from '../helpers/NumberHelpers';
import { getNumber } from './WindowHelpers';

var HELPER_HOVER_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0xffe032,
  transparent: true,
  opacity: 0.5,
  depthTest: false,
});
var HELPER_SELECT_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0xd34030,
  transparent: true,
  depthTest: false,
});
var HELPER_HIGHLIGHT_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0x70e9ff,
  transparent: true,
  depthTest: false,
});

var Mullion = function (renderer, window, geometry, material) {
  THREE.Mesh.call(this, geometry, material);

  var helpers = [];

  this.moveAll = function (newPosition) {
    var d = this.data;

    var oldPosition = d.def.Position / 1000;
    if (newPosition === oldPosition) {
      return;
    }

    var sashStart;
    var sashEnd;
    var sashLength;
    if (d.axis === 'y') {
      sashStart = d.sash.top;
      sashEnd = d.sash.bottom;
    } else if (d.axis === 'x') {
      sashStart = d.sash.left;
      sashEnd = d.sash.right;
    }
    sashLength = sashEnd - sashStart;

    var percentage = (newPosition - sashStart) / (oldPosition - sashStart);

    var mullions = d.clearanceMullions
      .filter(function (ele) {
        return ele.data.axis === d.axis;
      })
      .sort(function (a, b) {
        if (newPosition < oldPosition) {
          return a.data.def.Position - b.data.def.Position;
        }
        return b.data.def.Position - a.data.def.Position;
      });

    for (var i = 0; i < mullions.length; i++) {
      var mul = mullions[i];
      var sStart = d.axis === 'y' ? d.sash.top : d.sash.left;
      var sEnd = d.axis === 'y' ? d.sash.bottom : d.sash.right;
      var updatedPos =
        (mul.data.def.Position / 1000 - sStart) * percentage + sStart;
      updatedPos = Math.max(updatedPos, sStart);
      updatedPos = Math.min(updatedPos, sEnd);
      mul.data.def.Position = roundNumber(updatedPos * 1000, 1);
    }
  };

  this.move = function (newPos) {
    var width = this.data.endPos - this.data.startPos;
    if (newPos === undefined) newPos = this.data.currPos;

    var affectedMuls = [this];
    if (this.data.connectedObjects) {
      for (var i = 0; i < this.data.connectedObjects.length; i++) {
        affectedMuls.push(this.data.connectedObjects[i]);
      }
    }

    var minPos = -1e100,
      maxPos = 1e100;

    for (var i = 0; i < affectedMuls.length; i++) {
      var mul2 = affectedMuls[i];
      var d = mul2.data;

      var v1 = new THREE.Vector3();
      v1.setFromMatrixPosition(mul2.matrixWorld);
      v1.x = v1.x;
      v1.y = -v1.y;

      var currMinPos = d.startPos;
      var currMaxPos = d.endPos;

      var win = mul2.data.window;

      //Test if new position collides with any other mullion
      for (var i2 = 0; i2 < win.allProfiles.length; i2++) {
        var m2 = win.allProfiles[i2];
        if (m2 === mul2) continue;
        if (m2.data.axis !== mul2.data.axis) continue;

        var v2 = new THREE.Vector3();
        v2.setFromMatrixPosition(m2.matrixWorld);
        v2.x = v2.x;
        v2.y = -v2.y;

        //Check if positions collide
        if (Math.abs(v1.z - v2.z) + 1e-8 < (mul2.data.b + m2.data.b) / 2) {
          if (d.axis === 'y') {
            if (Math.abs(v1.x - v2.x) < (mul2.data.w + m2.data.w) / 2) {
              //Determine minimum or maximum position
              if (v1.y < v2.y) {
                currMaxPos = Math.min(currMaxPos, v2.y - m2.data.barWidth / 2);
              } else {
                currMinPos = Math.max(currMinPos, v2.y + m2.data.barWidth / 2);
              }
            }
          } else {
            if (Math.abs(v1.y - v2.y) < (mul2.data.h + m2.data.h) / 2) {
              //Determine minimum or maximum position
              if (v1.x < v2.x) {
                var testMaxPos =
                  mul2.data.oldPos + (v2.x - v1.x) - m2.data.barWidth / 2;
                currMaxPos = Math.min(currMaxPos, testMaxPos);
              } else {
                var testMinPos =
                  mul2.data.oldPos + (v2.x - v1.x) + m2.data.barWidth / 2;
                currMinPos = Math.max(currMinPos, testMinPos);
              }
            }
          }
        }
      }
      currMinPos += d.barWidth / 2;
      currMaxPos -= d.barWidth / 2;

      minPos = Math.max(minPos, currMinPos);
      maxPos = Math.min(maxPos, currMaxPos);
    }

    newPos = Math.max(newPos, minPos);
    newPos = Math.min(newPos, maxPos);

    newPos = roundNumber(newPos * 1000, 1);

    for (var i = 0; i < affectedMuls.length; i++) {
      var mul2 = affectedMuls[i];
      mul2.data.def.Position = newPos;
    }
  };

  this.highlightParallelMullions = function () {
    if (this.data.axis === 'y') {
      var v1 = new THREE.Vector3();
      v1.setFromMatrixPosition(this.matrixWorld);

      for (var winix = 0; winix < renderer.facade.windows.length; winix++) {
        for (
          var i1 = 0;
          i1 < renderer.facade.windows[winix].movableProfiles.length;
          i1++
        ) {
          var m2 = renderer.facade.windows[winix].movableProfiles[i1];
          if (this === m2) continue;

          var canMove = m2.data.axis === this.data.axis;
          if (!canMove) continue;

          var v2 = new THREE.Vector3();
          v2.setFromMatrixPosition(m2.matrixWorld);
          v2.y = v1.y;

          for (
            var i2 = 0;
            i2 < renderer.facade.windows[winix].movableProfiles.length;
            i2++
          ) {
            if (i1 === i2) continue;
            var m3 = renderer.facade.windows[winix].movableProfiles[i2];
            if (m3.data.axis !== this.data.axis) continue;
            var v3 = new THREE.Vector3();
            v3.setFromMatrixPosition(m3.matrixWorld);

            //Collision detection
            //Don't highlight if the movement would cause collision
            if (
              Math.abs(v2.y - v3.y) < (m2.data.h + m3.data.h) / 2 &&
              Math.abs(v2.x - v3.x) < (m2.data.w + m3.data.w) / 2
            ) {
              canMove = false;
              break;
            }
          }
          if (canMove) {
            renderer.interactionHandler.highlightObject(m2);
          }
        }
      }
    }
  };

  this.removeHelpers = function () {
    for (var i = 0; i < helpers.length; i++) {
      window.removeInteractableObject(helpers[i]);
      if (helpers[i].parent) {
        helpers[i].parent.remove(helpers[i]);
      }
    }
  };

  this.addHelper = function (
    container,
    mullionDef,
    clearanceBounds,
    secondaryAxisPos,
    zPos,
    type,
    text,
    geometry,
    calcPosition,
    setPosition
  ) {
    var d = this.data;
    var x;
    var y;
    var w = d.w;
    var h = d.h;
    var { left, right, top, bottom } = clearanceBounds;
    if (type === 'one_third') {
      if (d.axis === 'y') {
        x = secondaryAxisPos;
        y = (bottom - top) / 2 + top - calcPosition(2 / 3);
      } else if (d.axis === 'x') {
        x = (left - right) / 2 - left + calcPosition(1 / 3);
        y = secondaryAxisPos;
      } else {
        throw 'Invalid axis';
      }
    } else if (type === 'two_fifth') {
      if (d.axis === 'y') {
        x = secondaryAxisPos;
        y = (bottom - top) / 2 + top - calcPosition(3 / 5);
      } else if (d.axis === 'x') {
        x = (left - right) / 2 - left + calcPosition(2 / 5);
        y = secondaryAxisPos;
      } else {
        throw 'Invalid axis';
      }
    } else if (type === 'square') {
      if (d.axis === 'y') {
        x = secondaryAxisPos;
        y = (bottom - top) / 2 - h / 2 - w;
      } else if (d.axis === 'x') {
        x = (left - right) / 2 + w / 2 + h;
        y = secondaryAxisPos;
      } else {
        throw 'Invalid axis';
      }
    } else if (type === 'centered') {
      const mullions = d.clearanceMullions.filter(x => x !== this);

      if (d.axis === 'y') {
        let minY = top;
        let maxY = bottom;
        for (const mullion of mullions) {
          if (d.axis !== mullion.data.axis) {
            continue;
          }

          const intersectsOnAxis = !(
            d.absolutePosX - d.w / 2 >
              mullion.data.absolutePosX + mullion.data.w / 2 ||
            d.absolutePosX + d.w / 2 <
              mullion.data.absolutePosX - mullion.data.w / 2
          );
          if (!intersectsOnAxis) {
            continue;
          }

          if (mullion.data.absolutePosY > d.absolutePosY) {
            maxY = Math.min(
              maxY,
              mullion.data.absolutePosY - mullion.data.h / 2
            );
          } else if (mullion.data.absolutePosY < d.absolutePosY) {
            minY = Math.max(
              minY,
              mullion.data.absolutePosY + mullion.data.h / 2
            );
          }
        }

        const centerY = (minY + maxY) / 2;
        x = secondaryAxisPos;
        y = (bottom - top) / 2 + top - centerY;
      } else if (d.axis === 'x') {
        let minX = left;
        let maxX = right;
        for (const mullion of mullions) {
          if (d.axis !== mullion.data.axis) {
            continue;
          }

          const intersectsOnAxis = !(
            d.absolutePosY - d.h / 2 >
              mullion.data.absolutePosY + mullion.data.h / 2 ||
            d.absolutePosY + d.h / 2 <
              mullion.data.absolutePosY - mullion.data.h / 2
          );
          if (!intersectsOnAxis) {
            continue;
          }

          if (mullion.data.absolutePosX > d.absolutePosX) {
            maxX = Math.min(
              maxX,
              mullion.data.absolutePosX - mullion.data.w / 2
            );
          } else if (mullion.data.absolutePosX < d.absolutePosX) {
            minX = Math.max(
              minX,
              mullion.data.absolutePosX + mullion.data.w / 2
            );
          }
        }

        const centerX = (minX + maxX) / 2;
        x = (left - right) / 2 - left + centerX;
        y = secondaryAxisPos;
      } else {
        throw 'Invalid axis';
      }
    } else {
      throw 'Invalid mullion helper type';
    }

    const mw = getNumber(mullionDef, 'Width') / 1000;
    if (d.axis === 'y') {
      const mid = (top + bottom) / 2;
      if (mid + y - mw / 2 < top || mid + y + mw / 2 > bottom) {
        // Helper goes outside the sash, so we don't want to add it
        return;
      }
    } else if (d.axis === 'x') {
      const mid = (left + right) / 2;
      if (mid + x - mw / 2 < left || mid + x + mw / 2 > right) {
        // Helper goes outside the sash, so we don't want to add it
        return;
      }
    }

    var helperMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.5,
      depthTest: false,
    });
    var mesh = new THREE.Mesh(geometry, helperMaterial);
    mesh.position.x = x;
    mesh.position.y = y;
    mesh.position.z = zPos;
    mesh.data = d;
    mesh.objectType = 'mullion_helper';
    mesh.renderOrder = 1;
    window.addInteractableObject(mesh);
    container.add(mesh);

    mesh.onClick = () => {
      const pos =
        d.axis === 'y'
          ? top + (bottom - top) / 2 - y
          : left + (right - left) / 2 + x;
      setPosition(pos);

      renderer.interactionHandler.clearSelectedObjects();
      renderer.facade.rebuild();
      return true;
    };
    mesh.onHover = () => {
      mesh.material = HELPER_HOVER_MATERIAL;
    };
    mesh.onUnhover = () => {
      mesh.material = helperMaterial;
    };

    helpers.push(mesh);

    mesh.buildEdgesNoDepthTest();
    mesh.buildText(text, 30);
  };
};

Mullion.prototype = Object.create(THREE.Mesh.prototype);

export { Mullion };
